import React, { useState, useEffect, useRef, useMemo, MouseEvent } from 'react';
import {
  Box, Grid, Card, CardContent, Typography,
  IconButton, Button, Divider, Stack,
  Drawer, Chip, TextField, Collapse,
  CardMedia,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Badge
} from '@mui/material';
import { Add, Remove, RestaurantMenu, ExpandMore, ExpandLess } from '@mui/icons-material';
import { AnimatePresence, motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';


import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Controller, FieldArray, FieldArrayMethodProps, FieldPath, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Paper from '@mui/material/Paper';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import ClearIcon from '@mui/icons-material/Clear';

import MoreIcon from '@mui/icons-material/More';

import BoltIcon from '@mui/icons-material/Bolt';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';

import { ChevronLeft, ChevronRight } from '@mui/icons-material';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import useUtils from 'library/utils';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  currentUserSessionAtom, isWaitingBoxShowAtom, allProductsAtom,
  allProductsWithOptionsAtom, currentFormNameAtom, isSaveLoadingAtom, isSearchBoxShowAtom,
  currentBasicTextFilterPropsAtom,
  colorsAtom
} from 'library/store';

import useProductService, { useBasicFilterProduct, useBasicFilterProductWithOptions } from 'features/setup/services/Product';

import useBillingService, { useBasicFilterBilling } from './services/Billing';
import useTellerOperationService from './services/TellerOperation';

import { IBilling, defaultBilling, IBillingDetail, defaultBillingDetail, ISale, defaultSale, IBillingCommand, defaultBillingCommand } from './models/Billing';

import { globalConfig } from 'config';

import { defaultProductOption, IProduct, IProductOption, ProductType } from 'features/setup/models/Product';

import useEnumerationService, { useBasicFilterEnumeration, useEnumerationItemCreateUpdate, } from 'features/configuration/services/Enumeration';

import IEnumeration, {
  IEnumerationItem, Enum_SERVICE_TASK, Enum_OPERATION_STATUS, Enum_BILLING_STATUS_PURPOSE,
  Enum_ARTICLE_OPTION, Enum_ARTICLE_OPTION_CLASS, Enum_PRICE_PURPOSE,
  Enum_MAINTENANCE_CATEGORY, Enum_CONTRACT_SCOPE, Enum_CONTRACT_TYPE, Enum_CORPORATION_TYPE,
  Enum_RENTAL_PERIODICITY,
  Enum_CURRENCY, Enum_PAYMENT_SOURCE,
  Enum_CONSUMPTION_OPTION,
  Enum_BILLING_TERM_REFERENCE,
  defaultEnumerationItem,
  Enum_DEFAULT_BILLING_REFERENCE,
  Enum_LINE_OF_BUSINESS
} from 'features/configuration/models/Enumeration';

import { debounce, sum } from 'lodash';
import { format } from "date-fns";
import { IStore, defaultStore } from 'features/configuration/models/Store';
import { isFalsy } from 'utility-types';
import { getValue } from '@testing-library/user-event/dist/utils';
import { IResult } from 'library/interface';
import { EditBillingDetailDialog } from './EditBillingDetailDialog';
import { MdSystemUpdateAlt } from 'react-icons/md';
import { StatusChipIcon } from 'components/ui/StatusChipIcon';
import { FormDialog } from 'components/ui/FormDialog';
import { EditBillingDiscountDialog } from './EditBillingDiscountDialog';

const MotionChip = motion(Chip);

/* ------------------ Main Component ------------------ */
export const BillingCommandForm = () => {
  const theme = useTheme();
  //const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [isWaitingBoxShow, setIsWaitingBoxShow] = useRecoilState(isWaitingBoxShowAtom);

  //const { billingId } = useParams<{ billingId: number}>();
  const { id } = useParams();

  const { language: lg, taskCodes, userDescription, userName, userAlias,
    employeeId, roleEntities, applicationSetup, userStores } = useRecoilValue(currentUserSessionAtom);

  const allProducts = useRecoilValue(allProductsAtom);
  const [allProductsWithOptions, setAllProductsWithOptions] = useRecoilState(allProductsWithOptionsAtom);

  const { range, addDurationInPeriod } = useUtils();

  const [_id, _setId] = useState<number>(Number(id || 0));

  const { retrieveEntity, retrieveData, openEntityActionDrawer, openEntityPrintDrawer, checkEntitySaveAuthorization } = entityService();

  const { createBillingCommand, updateBillingCommand, evaluateAddedProduct, getBillingWithCommand,
    sumBillingCommand, sumBilling } = useBillingService();

  const { getTellerOperationsByBilling } = useTellerOperationService();

  const { getEnumerationItemsByEnumerationCodes, getAsOptions } = useEnumerationService();
  //const { getDefaultProductType } = useApplicationSetupService();

  const { getBasicProducts, getBasicProductWithOptions, getProductsWithOptions, getProductTypeNumber } = useProductService();

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);


  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterBilling = useBasicFilterBilling(
    (event: React.MouseEvent<unknown>, row: IBilling) => {
      setIsSearchBoxShow(false);
      _setId(row.id);
    }
  );

  const emptyFunc = (obj: any) => { }


  const [currentProductOptions, setCurrentProductOptions] = useState<IProductOption[]>([]);
  const [productFilters, setProductFilters] = useState<IProduct[]>([]);



  const dt = new Date();
  const methods = useForm<IBilling>({
    defaultValues: {
      ...defaultBilling,
      currencyCode: applicationSetup.defaultCurrencyCode, defaultTaxRate: applicationSetup.defaultTaxRate,
      showroomId: userStores.find(s => s.isShowroom)?.storeId ?? 0,
      reference: `${userAlias ?? ''}-${format(dt, 'yyyy/MM/dd-HH')}h${format(dt, 'mm')}`
    }
  });
  const { register, setValue, getValues, watch, reset, handleSubmit, control, formState: { errors } } = methods;

  // let { fields: billingDetails, append: appendBillingDetails, update: updateBillingDetail, remove: removeBillingDetail, } = useFieldArray({//<O, TName>({ //<O,`billingDetails.${number}.billingDetailTasks`>({
  //   name: `billingDetails`,
  //   control,
  // });


  const methodsCommand = useForm<IBillingCommand>({
    defaultValues: {
      ...defaultBillingCommand, status: '95',
      reference: `${userAlias ?? ''}-${format(dt, 'yyyy/MM/dd-HH')}h${format(dt, 'mm')}`,

      showroomId: userStores.find(s => s.isShowroom)?.storeId ?? 0,
      currencyCode: applicationSetup.defaultCurrencyCode, defaultTaxRate: applicationSetup.defaultTaxRate
    }
  });
  const { register: registerCommand, setValue: setValueCommand, watch: watchCommand,
    reset: resetCommand, control: controlCommand,
    getValues: getValuesCommand } = methodsCommand;
  let { fields: billingDetails, append: appendBillingDetails, update: updateBillingDetail, remove: removeBillingDetail, } = useFieldArray({//<O, TName>({ //<O,`billingDetails.${number}.billingDetailTasks`>({
    name: `billingDetails`,
    control: controlCommand
  });


  const queryClient = useQueryClient();
  const { isLoading, isError, isSuccess, error, mutate } = useMutation<IResult<IBillingCommand>, Error, IBillingCommand>(
    getValuesCommand().id > 0 ? updateBillingCommand : createBillingCommand, {
    onSuccess: async (data: IResult<IBillingCommand>) => {
      enqueueSnackbar(t('_Operation_done'), {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });


      setIsSaveLoading(false);
      //setValue('id', data.data.id);
      // _setId(data.data.id);
      // //retrieveData('Billing',data.data.id, refetch);
      // setCurrentFormNameAtom(`${t('Billing')} - # ${data.data.billingId} # `);

      await loadData(data.data.billingId);

      //queryClient.invalidateQueries(['Billing',data.data.id]);
    },
    onError: (err: Error) => {
      enqueueSnackbar(error?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setIsSaveLoading(false);
    }
  });


  const { data: _data, refetch } = useQuery<IBilling>(['Billing', _id], () => retrieveEntity('Billing', _id, 'Full'),
    { refetchOnWindowFocus: false, enabled: false });

  const { data: enumItems, refetch: refetchEnumerationItems } = useQuery<IEnumerationItem[]>(['EnumerationItems', 'Billing'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_OPERATION_STATUS, Enum_BILLING_STATUS_PURPOSE, Enum_BILLING_TERM_REFERENCE, Enum_PAYMENT_SOURCE,
      Enum_PRICE_PURPOSE, Enum_CURRENCY, Enum_CORPORATION_TYPE, Enum_DEFAULT_BILLING_REFERENCE, Enum_LINE_OF_BUSINESS]));

  const { data: enumItems2 } = useQuery<IEnumerationItem[]>(['EnumerationItems2', 'Billing'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_SERVICE_TASK,
      Enum_ARTICLE_OPTION, Enum_ARTICLE_OPTION_CLASS, Enum_CONSUMPTION_OPTION,
      Enum_MAINTENANCE_CATEGORY, Enum_CONTRACT_SCOPE, Enum_CONTRACT_TYPE, Enum_RENTAL_PERIODICITY]));

  const { data: store } = useQuery<IStore>(['Billing', 'Store', getValues().showroomId], () =>
    retrieveEntity('Store', getValues().showroomId), { refetchOnWindowFocus: false, enabled: getValues().showroomId > 0 });

  const [openProductFilter, setOpenProductFilter] = useState(false); // 1
  const basicFilterProduct = useBasicFilterProductWithOptions(0, getValues().showroomId, getValues().currencyCode,
    async (event: React.MouseEvent<unknown>, row: IProduct) => {
      const { id, description, name, serviceDurationInMinute, isActive, lineOfBusinessCode, productOptions } = row;

      if (!isActive) {
        enqueueSnackbar(`${t('This product is not active')} : ${name}`, {
          variant: 'warning',
          anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
        });
        return;
      }

      // Add product by handle click...
      handleProductClick(id);
      setOpenProductFilter(false);
    }
  );

  const watchIsDirectSale = watch('isDirectSale');

  const watchDefaultTaxRate = watch('defaultTaxRate');
  const watchShowroomId = watch('showroomId');
  const watchCurrencyCode = watch('currencyCode');

  const watchCustomerId = watch('customerId');

  const watchDiscount = watch('discount');

  const watchIsProforma = watch('isProforma');

  const methodsFilter = useForm<{ name: string, code: string }>({ defaultValues: { code: '', name: '' } });
  const { register: registerFilter, setValue: setValueFilter, getValues: getValuesFilter, reset: resetFilter, watch: watchFilter, control: controlFilter } = methodsFilter;

  const watchProductFilterName = watchFilter('code');
  const watchProductFilterCode = watchFilter('name');


  const [lineOfBusinessCode, setLineOfBusinessCode] = useState<string>(applicationSetup.firstLineOfBusinessCode);
  const refLineOfBusinessCode = useRef<string>(applicationSetup.firstLineOfBusinessCode);

  // const handleLineOfBusiness = async (newLineOfBusinessCode: string) => {
  //   if (newLineOfBusinessCode === null) return;
  //   setLineOfBusinessCode(newLineOfBusinessCode);
  //   refLineOfBusinessCode.current = newLineOfBusinessCode; //getProductTypeNumber(newProductType);

  //   const code = getValuesFilter().code;
  //   const name = getValuesFilter().name;

  //   const arr = (allProducts.length > 0 && applicationSetup.loadProductOnLogon) ?
  //     allProducts.filter(p => p.code.toLowerCase().includes(code.toLowerCase()) &&
  //       p.name.toLowerCase().includes(name.toLowerCase()) &&
  //       p.lineOfBusinessCode.includes(refLineOfBusinessCode.current))
  //     : await getBasicProducts(0, getValues().showroomId, getValues().currencyCode, {
  //       isActive: true,
  //       code: getValuesFilter().code,
  //       name: getValuesFilter().name,
  //       description: '',
  //       lineOfBusinessCode: refLineOfBusinessCode.current
  //     }, { pageSize: 150, pageNumber: 1 });

  //   setProductFilters(arr);
  // };

  const handleProductFilter = ({ code, name, lineOfBusinessCode }:
    { code: string, name: string, lineOfBusinessCode: string }) => {
    setSearchCode(code);
    setSearchName(name);
    setLineOfBusinessCode(lineOfBusinessCode);

    debouncedProductFilter({ code, name, lineOfBusinessCode });
  }

  const debouncedProductFilter = useRef(
    debounce(async ({ code, name, lineOfBusinessCode }: { code: string, name: string, lineOfBusinessCode: string }) => {
      //const { customerId, customerFirstName, customerLastName, customerCorporationName, customerPersonType } = getValues();

      // const code = getValuesFilter().code;
      // const name = getValuesFilter().name;

      const arr = (allProducts.length > 0 && applicationSetup.loadProductOnLogon) ?
        allProducts.filter(p => p.code.toLowerCase().includes(code.toLowerCase()) &&
          p.name.toLowerCase().includes(name.toLowerCase()) &&
          p.lineOfBusinessCode.includes(lineOfBusinessCode))
        : await getBasicProducts(0, getValues().showroomId, getValues().currencyCode, {
          isActive: true,
          code: getValuesFilter().code,
          name: getValuesFilter().name,
          description: '',
          lineOfBusinessCode: lineOfBusinessCode
        }, { pageSize: 150, pageNumber: 1 });

      setProductFilters(arr);

    }, 100)
  ).current;

  const [showProductOption, setShowProductOption] = useState<boolean>(false);
  const [selectedProductOption, setSelectedProductOption] = useState<IProductOption>(defaultProductOption);

  const handleProductClick = async (productId: number) => {
    //if(billingDetails.some(bd => bd.productId === productId)) return;
    if (getValues().status !== '95') {
      enqueueSnackbar(`${t('You can not add product, because this is billing is already validated')}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      return;
    }

    const exists = allProductsWithOptions.some(p => p.id === productId);
    const prod = exists ? allProductsWithOptions.find(p => p.id === productId)! :
      await getBasicProductWithOptions(productId, getValues().showroomId, getValues().currencyCode);
    //productFilters.find(p => p.id === productId);
    if (!exists)
      setAllProductsWithOptions([...allProductsWithOptions, { ...prod }]);

    if (isFalsy(prod)) return;

    const { id, description, name, serviceDurationInMinute, isActive, filterOption, lineOfBusinessCode, type,
      defaultPrice, defaultMaxDiscount, defaultMaxLoaded, priceExpression, maxDiscountExpression, maxLoadedExpression,
      productOptions, isTaxable, pictureUrl } = prod;

    const st = store ? store! : defaultStore;

    if (!isActive) {
      enqueueSnackbar(`${t('This product is not active')} : ${name}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });
      return;
    }

    if (['article'].includes(type) && productOptions.length <= 0) {
      enqueueSnackbar(`${t('This product must have at least one option')} : ${name}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });
      return;
    }

    if (!productOptions.some(opt => opt.isAvailable) && st.explicitProductDefinition) {
      enqueueSnackbar(`${t('This product must have at least one option')} : ${t('availability')} : ${name}`, {
        variant: 'warning',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });
      return;
    }

    setCurrentProductOptions(productOptions);
    if (productOptions.length > 1) {
      setShowProductOption(true);
    } else {
      await handleProductOptionChoiceClick(productOptions.at(0) ?? { ...defaultProductOption, productId: id });
    }

    setValueFilter('code', '');
    setValueFilter('name', '');
  }

  const [openMessageBox, setOpenMessageBox] = useState<boolean>(false);
  const [messageMessageBox, setMessageMessageBox] = useState<string>('');

  const handleProductOptionChoiceClick = async (productOption: IProductOption) => {

    const { productId } = productOption;

    const exists = allProductsWithOptions.some(p => p.id === productId);

    const prod = exists ? allProductsWithOptions.find(p => p.id === productId)! :
      await getBasicProductWithOptions(productId, getValues().showroomId, getValues().currencyCode);
    //productFilters.find(p => p.id === productId);
    if (!exists)
      setAllProductsWithOptions([...allProductsWithOptions, { ...prod }]);

    //const prod = productFilters.find(p => p.id === productOption.productId);
    if (isFalsy(prod)) return;


    if (getValuesCommand().billingDetails.some(bd => bd.productId === prod.id && bd.productOptionId === productOption.id)) {

      const billingDetail = getValuesCommand().billingDetails.find(bd =>
        bd.productId === prod.id && bd.productOptionId === productOption.id);
      updateBillingDetail(getValuesCommand().billingDetails.indexOf(billingDetail!), {
        ...billingDetail, quantity: (billingDetail!.quantity || 0) + 1
      } as unknown as IBillingDetail);
      // setSelectedProductOption(productOption);
      // setMessageMessageBox('This option alredy exist for this product, do you want to add another ?');
      // setOpenMessageBox(true);

    } else {

      addProductOptionChoice(productOption);
    }

    setShowProductOption(false);
  }

  const addProductOptionChoice = async (productOption: IProductOption) => {

    const { productId } = productOption;

    const exists = allProductsWithOptions.some(p => p.id === productId);
    const prod = exists ? allProductsWithOptions.find(p => p.id === productId)! :
      await getBasicProductWithOptions(productId, getValues().showroomId, getValues().currencyCode);

    //productFilters.find(p => p.id === productId);
    if (!exists)
      setAllProductsWithOptions([...allProductsWithOptions, { ...prod }]);

    // const prod = productFilters.find(p => p.id === productOption.productId);
    if (isFalsy(prod)) return;

    const { id, description, name, serviceDurationInMinute, isActive, filterOption, lineOfBusinessCode, type,
      defaultPrice, defaultMaxDiscount, defaultMaxLoaded, priceExpression, maxDiscountExpression, maxLoadedExpression,
      productOptions, isTaxable, pictureUrl } = prod;

    const st = store ? store! : defaultStore;

    const articleOptions = refEnumItems2.current?.filter(x => x.enumerationCode === Enum_ARTICLE_OPTION &&
      x.parentEnumerationItemCode === filterOption);
    const articleOption = (articleOptions && articleOptions.length === 1) ? articleOptions[0].code || '' : '';

    const tax = isTaxable ? (getValues().defaultTaxRate * defaultPrice / 100) : 0;

    const countBillingDetails = getValuesCommand().billingDetails.length;

    if (['article', 'fee', 'contract', 'maintenance', 'consumption', 'formation', 'service', 'package'].includes(type) &&
      isFalsy(priceExpression) && isFalsy(maxDiscountExpression) && isFalsy(maxLoadedExpression)) {

      appendBillingDetails({
        ...defaultBillingDetail, id: 0, type, articleId: id, productId: prod.id,
        productOptions, quantity: 1,
        unityAmount: defaultPrice, baseAmount: defaultPrice, netAmount: defaultPrice, tax, amount: defaultPrice + tax,
        maxDiscountAmount: defaultMaxDiscount, maxLoadedAmount: defaultMaxLoaded,
        productName: name, lineOfBusinessCode,
        productOptionId: productOption.id,
        articleFilterOption: filterOption, articleOption, productPictureUrl: pictureUrl
      } as unknown as IBillingDetail);
    } else {

      setIsWaitingBoxShow(true);
      const res = await evaluateAddedProduct({
        ...getValues(), productId: id, billingDetail: { ...defaultBillingDetail, quantity: 1 },
        currencyCode: watchCurrencyCode, storeId: watchShowroomId
      });
      setIsWaitingBoxShow(false);

      const bilDetail = res.data;

      const bilServiceTasks = (type === 'service' && employeeId > 0) ?
        bilDetail.billingServiceTasks.map(({ taskCode, ...bst }) =>
        ({
          ...bst, employeId: taskCodes.includes(taskCode) ? employeeId : 0,
          employeeFullName: taskCodes.includes(taskCode) ? `${userDescription} - ${userName}` : ''
        })) :
        bilDetail.billingServiceTasks;

      const bilPackagingDetails = (type === 'packaging' && employeeId > 0) ?
        bilDetail.billingPackagingDetails.map(({ ...bpd }) =>
        ({
          ...bpd, billingServiceTasks: bpd.billingServiceTasks.map(({ taskCode, ...bst }) =>
          ({
            ...bst, employeId: taskCodes.includes(taskCode) ? employeeId : 0,
            employeeFullName: taskCodes.includes(taskCode) ? `${userDescription} - ${userName}` : ''
          }))
        })) :
        bilDetail.billingPackagingDetails;

      appendBillingDetails({
        ...bilDetail, type, articleId: id, productId: prod.id,
        productOptions, quantity: 1,
        unityAmount: defaultPrice, baseAmount: defaultPrice, netAmount: defaultPrice, tax, amount: defaultPrice + tax,
        maxDiscountAmount: defaultMaxDiscount, maxLoadedAmount: defaultMaxLoaded,
        productName: name, lineOfBusinessCode,
        productOptionId: productOption.id,
        articleFilterOption: filterOption, articleOption, productPictureUrl: pictureUrl,
        billingServiceTasks: bilServiceTasks, billingPackagingDetails: bilPackagingDetails
      } as unknown as IBillingDetail);

    }
    setValue('currentBillingDetailIndex', countBillingDetails);
    //setTotalAmount();
    setOpenMessageBox(false);

    if ((articleOptions || []).length > 1)
      setBillingDetailOpen(true);
  }

  

  const setTotalAmount = () => {
    const netAmount = sumBilling(getValues(), getValuesCommand(), 'netAmount');
    const tax = sumBilling(getValues(), getValuesCommand(), 'tax');
    const amount = sumBilling(getValues(), getValuesCommand(), 'amount');

    const dis = sumBilling(getValues(), getValuesCommand(), 'amount');

    // const net = sumNetAmount - getValues().discount;
    // const tax = sumNetAmount === 0 ? 0 : (net * sumTaxAmount) / sumNetAmount;

    // const amount = net + tax;

    setValue('initialNetAmount', netAmount - getValues().discount);
    setValue('netAmount', netAmount);
    setValue('tax', tax);
    setValue('amount', amount);
    setValue('unpaidAmount', amount - getValues().amountPaid);
  }

  const [billingDetailOpen, setBillingDetailOpen] = useState<boolean>(false);
  const [selectedBillingDetail, setSelectedBillingDetail] = useState<IBillingDetail | null>(null);

  const handleViewBillingDetail = (detail: IBillingDetail) => {
    setSelectedBillingDetail(detail);
    setBillingDetailOpen(true);
  }

  const handleCloseBillingDetail = () => {
    setBillingDetailOpen(false);
    setSelectedBillingDetail(null);
  };

  const handleSaveBillingDetail = (updatedDetail: IBillingDetail) => {
    const command = getValuesCommand();
    resetCommand({
      ...command,
      billingDetails: command.billingDetails.map((d) =>
        d.productId === updatedDetail.productId ? updatedDetail : d)
    });

    handleCloseBillingDetail();
    setTotalAmount();
  };


  const [billingCommandOpen, setBillingCommandOpen] = useState<boolean>(false);
  const [selectedBillingCommand, setSelectedBillingCommand] = useState<IBillingCommand | null>(null);

  const handleViewBillingCommand = (command: IBillingCommand) => {
    setSelectedBillingCommand(command);
    setBillingCommandOpen(true);
  };

  const handleCloseDetail = () => {
    setBillingCommandOpen(false);
    setSelectedBillingCommand(null);
  };

  const handleActionCommand = (commandId: number) => {
    openEntityActionDrawer('BillingCommand', commandId);
  }

  const handlePrintCommand = (commandId: number) => {
    openEntityPrintDrawer('BillingCommand', commandId);
  }


  const [billingDiscountOpen, setBillingDiscountOpen] = useState<boolean>(false);
  //const [selectedBillingDetail, setSelectedBillingDetail] = useState<IBillingDetail | null>(null);

  const handleViewBillingDiscount = (billing: IBilling) => {

    setBillingDiscountOpen(true);
  }

  const handleCloseBillingDiscount = () => {
    setBillingDiscountOpen(false);

  };

  const handleSaveBillingDiscount = (updatedBilling: IBilling) => {
    const billing = getValues();
    console.log(updatedBilling);
    reset({
      ...billing,
      discount: updatedBilling.discount
    });

    handleCloseBillingDetail();
    setTotalAmount();
  };

  const [catalogOpen, setCatalogOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [searchCode, setSearchCode] = useState('');
  const [searchName, setSearchName] = useState('');

  //const draftCommand = commands.find(c => c.status === 'draft');

  /* ------------------ Actions ------------------ */
  // const addProductToCurrentCommand = (product: any) => {

  //   setCommands(prev => {
  //     let currentCommand = prev.find(c => c.status === '95');
  //     if (!currentCommand) {
  //       currentCommand = { id: 0, status: '95', billingDetails: [], 
  //             commandDate: new Date(), reference: `${userAlias} - ${prev.length + 1}`, 
  //             expanded: true};

  //       prev = [currentCommand, ...prev];
  //     }

  //     const found = currentCommand.billingDetails.find((i: any) => i.product.id === product.id);
  //     if (found) found.quantity += 1;
  //     else currentCommand.billingDetails.push({ product, quantity: 1 });
  //     return [...prev];
  //   });
  // };

  const incQty = (productId: number) => {
    const command = getValuesCommand();
    setValueCommand('billingDetails',
      command.billingDetails.map((i: IBillingDetail) =>
        i.productId === productId ? { ...i, quantity: i.quantity + 1 } : i));

    setTotalAmount();
  }

  const decQty = (productId: number) => {
    const command = getValuesCommand();
    setValueCommand('billingDetails',
      command.billingDetails.flatMap((i: IBillingDetail) => {
        if (i.productId !== productId) return [i];

        // If quantity > 1 → decrement
        if ((i.quantity ?? 0) > 1) {
          return [{ ...i, quantity: i.quantity - 1 }];
        }

        // If quantity === 1 → remove line
        return [];
      }),
      { shouldDirty: true }
    );

    setTotalAmount();
  };

  // const decQty = (productId: number) =>
  //   setCommands(prev => prev.map(c => c.id === commandId ? { ...c, items: c.items.flatMap((i: any) => i.product.id === productId ? (i.qty > 1 ? [{ ...i, qty: i.qty - 1 }] : []) : [i]) } : c));

  // const sendDraftToKitchen = (commandId: number) =>
  //   setCommands(prev => prev.map(c => c.id === commandId ? { ...c, status: 'sent' } : c));

  // const createNewDraft = () => {
  //   setCommands(prev => [{ id: prev.length + 1, status: 'draft', items: [], date: new Date(), reference: `BILL${prev.length + 1}`, customer: '', expanded: true }, ...prev]);
  // };

  const toggleExpandCommand = (id: number) => { }
  // setCommands(prev => prev.map(c => c.id === id ? { ...c, expanded: !c.expanded } : c));

  /* ------------------ Filtering Products ------------------ */
  // const filteredProducts = useMemo(() => {
  //   return PRODUCTS.filter(p => {
  //     if (categoryFilter && p.category !== categoryFilter) return false;
  //     if (searchText && !p.name.toLowerCase().includes(searchText.toLowerCase())) return false;
  //     return true;
  //   })
  // }, [categoryFilter, searchText]);

  /* ------------------ Totals ------------------ */
  const subtotal = 0; //commands.flatMap(c => c.items).reduce((s, i) => s + i.quantity * i.product.price, 0);
  const tax = subtotal * 0.18;
  const total = subtotal + tax;


  // useEffect(() => {

  //   debouncedProductFilter();

  // }, [lineOfBusinessCode]);

  const refEnumItems = useRef<IEnumerationItem[]>();
  useEffect(() => {
    refEnumItems.current = enumItems;

  }, [enumItems]);

  const refEnumItems2 = useRef<IEnumerationItem[]>();
  useEffect(() => {
    refEnumItems2.current = enumItems2;

  }, [enumItems2]);

  useEffect(() => {
    setCurrentFormNameAtom(`${t('Billing')} - # ${_id > 0 ? _id : t('New')} #`);
    setCurrentBasicTextFilterProps(basicFilterBilling);
  }, []);


  useEffect(() => {

    setCurrentFormNameAtom(_id > 0 ? `${t('Billing')} # ${_id} #` : t('Billing'));

  }, [_id]);


  useEffect(() => {
    async function _() {
      if (_id > 0) {
        await loadData(_id);
      }
    }

    _();

  }, [_id]);

  async function loadData(billingId: number) {
    const billing = await getBillingWithCommand(billingId);
    if (isFalsy(billing)) return;

    reset({ ...billing });

    const command95 = billing.billingCommands.find(c => c.status === '95');
    resetCommand(command95 ? { ...command95 } : {
      ...defaultBillingCommand,
      status: '95',
      reference: `${userAlias ?? ''}-${format(dt, 'yyyy/MM/dd-HH')}h${format(dt, 'mm')}`,

      showroomId: userStores.find(s => s.isShowroom)?.storeId ?? 0,
      currencyCode: applicationSetup.defaultCurrencyCode, defaultTaxRate: applicationSetup.defaultTaxRate
    });

  }

  const newData = async (event: MouseEvent<HTMLButtonElement>) => {
    _newData();
  }

  const _newData = () => {
    _setId(0);
    reset({
      ...defaultBilling, showroomId: userStores.find(s => s.isShowroom)?.storeId ?? 0,
      currencyCode: applicationSetup.defaultCurrencyCode, defaultTaxRate: applicationSetup.defaultTaxRate
    });
  }



  const saveData = async (event: MouseEvent<HTMLButtonElement>) => {
    
    if (!checkEntitySaveAuthorization('BillingCommand', _id)) {
      setIsSaveLoading(false);
      return;
    }
    const billingCommand = getValuesCommand();

    const billing = getValues();

  const calcNetAmount = sumBilling(billing, billingCommand, 'netAmount');
  const calInitialNetAmount = sumBilling(billing, billingCommand, 'initialNetAmount');
  const calcDiscount = sumBilling(billing, billingCommand, 'discount');
  const calcTax = sumBilling(billing, billingCommand, 'tax');

  const calcAmount = sumBilling(billing, billingCommand, 'amount');

    mutate({
      ...billingCommand, billingId: _id,
      billingDetails: billingCommand.billingDetails?.map(detail => {
        
        const base = detail.unityAmount * detail.quantity;
        const net = (detail.unityAmount - detail.discount + detail.loaded)*detail.quantity;

        return {
        ...detail, id: detail.id ? Number(detail.id) : 0,
        netAmount: net,
        baseAmount: base,
        amount: net + detail.tax,     
        } }),
      billingNetAmount: calcNetAmount,   billingTax: calcTax,
      billingDiscount: calcDiscount, billingAmount: calcAmount,
    });
  }

  const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityActionDrawer('Billing', _id);
  }

  const printData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityPrintDrawer('Billing', _id);
  }

  const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {
    // queryClient.invalidateQueries(['Billing', _id]);
    // await retrieveData('Billing', _id, refetch);
    // reset(_data);
    await loadData(_id);
  }

  /* ------------------ Layout ------------------ */
  return (
    <Box p={2}>
      <Grid container spacing={2}>

        {/* DESKTOP: Product Catalog */}
        {!isCompactLayout && (
          <Grid item md={7}>
            <ProductCatalog key={`${lineOfBusinessCode} - ${searchCode} - ${searchName}`}
              onProductFilter={handleProductFilter}
              products={productFilters}
              billingDetails={getValuesCommand().billingDetails}
              categories={(enumItems || []).filter(x => x.enumerationCode === Enum_LINE_OF_BUSINESS)}
              lineOfBusinessCode={lineOfBusinessCode}

              searchCode={searchCode}
              searchName={searchName}

              onAdd={(p: any) => { handleProductClick(p.id); }}
              currencyCode={getValues().currencyCode} />
          </Grid>
        )}

        {/* Commands + Billing */}
        <Grid item xs={12} md={5}>
          {/* <Button variant="outlined" fullWidth sx={{ mb:1 }} onClick={newData}>
            + New Command
          </Button>  */}
          <Button id='btnNew' onClick={newData} sx={{ display: 'none' }} />
          <Button id='btnSave' onClick={saveData} sx={{ display: 'none' }} />
          <Button id='btnAction' onClick={actionData} sx={{ display: 'none' }} />
          <Button id='btnAfterAction' onClick={afterAction} sx={{ display: 'none' }} />

          <AnimatePresence>
            {
              <motion.div
                key={`${getValuesCommand().id}-${getValuesCommand().status}`} initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <CommandBlock
                  command={{ ...getValuesCommand() }}
                  editable
                  inc={incQty}
                  dec={decQty}
                  openDetail={handleViewBillingDetail}
                  actionCommand={handleActionCommand}
                  printCommand={handlePrintCommand}
                  isCompactLayout={isCompactLayout}
                  onOpenCatalog={() => setCatalogOpen(true)}
                // sendToKitchen={sendDraftToKitchen}
                // toggleExpand={toggleExpandCommand}
                />
                <EditBillingDetailDialog
                  open={billingDetailOpen}
                  detail={selectedBillingDetail}
                  onClose={handleCloseBillingDetail}
                  onSave={handleSaveBillingDetail}

                  enumItems={enumItems}
                />
              </motion.div>
            }

            <CommandList
              commands={getValues().billingCommands.filter(c => c.status !== '95')}
              sumBillingCommand={sumBillingCommand}
              handleActionCommand={handleActionCommand}
              handlePrintCommand={handlePrintCommand}
              onViewDetail={handleViewBillingCommand}
            />
            {/* {getValues().billingCommands.filter(c => c.status !== '95').map((c, idx) => (
              <motion.div key={`${c.id}-${idx}`} initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <CommandRow
                  key={c.id}
                  command={c}
                  sumBillingCommand={sumBillingCommand}
                  onAction={handleActionCommand}
                  onPrint={handlePrintCommand}
                />
              </motion.div>
            ))} */}
          </AnimatePresence>

          <BillingSummary key={`${getValuesCommand().billingDetails.map(x => String(x.amount)).join('-')}`}
            billing={getValues()} billingCommand={getValuesCommand()} onViewDiscount={handleViewBillingDiscount} />
          <EditBillingDiscountDialog
            open={billingDiscountOpen}
            billing={getValues()}
            onClose={handleCloseBillingDiscount}
            onSave={handleSaveBillingDiscount}
          />
        </Grid>
      </Grid>

      {/* MOBILE DRAWER */}
      {isCompactLayout && (
        <>
          {/* <Button
            variant="contained"
            sx={{ position: 'fixed', bottom: 80, right: 16 }}
            startIcon={<RestaurantMenu />}
            onClick={() => setCatalogOpen(true)}
          >
            {`${t('Products')}/${t('Services')}`}
          </Button> */}

          <Drawer
            open={catalogOpen} onClose={() => setCatalogOpen(false)}
            anchor="bottom"

            PaperProps={{
              sx: {
                width: "100%",
                height: "100dvh",        // mobile-safe full height
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              },
            }}>
            {/* HEADER */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography fontWeight={700}>
                {`${t("Products")}/${t("Services")}`}
              </Typography>

              <IconButton onClick={() => setCatalogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box p={2} key={`${lineOfBusinessCode} - ${searchCode} - ${searchCode}`}>
              <ProductCatalog
                onProductFilter={handleProductFilter}
                products={productFilters}
                billingDetails={getValuesCommand().billingDetails}
                categories={(enumItems || []).filter(x => x.enumerationCode === Enum_LINE_OF_BUSINESS)}
                lineOfBusinessCode={lineOfBusinessCode}

                searchCode={searchCode}
                searchName={searchName}

                onAdd={(p: any) => { handleProductClick(p.id); /*setCatalogOpen(false)*/ }}

                currencyCode={getValues().currencyCode}

              />
            </Box>
          </Drawer>
        </>
      )}
      {billingCommandOpen && <FormDialog open={billingCommandOpen} maxWidth='sm'
        okText='' cancelText='' title={t('Command')} onCancel={() => { }}
        onClose={() => { setBillingCommandOpen(false); }} onOk={() => { setBillingCommandOpen(false); }}  >
        <CommandBlock command={selectedBillingCommand || defaultBillingCommand} editable={false}
          openDetail={handleViewBillingDetail} actionCommand={handleActionCommand}
          printCommand={handlePrintCommand} />
      </FormDialog>}
    </Box>
  );
}

/* ------------------ Product Catalog ------------------ */
function ProductCatalog({ onProductFilter, products, categories, lineOfBusinessCode, searchCode, searchName,
  onAdd, onClose, currencyCode, billingDetails }: {
    onProductFilter: ({ code, name, lineOfBusinessCode }: { code: string, name: string, lineOfBusinessCode: string }) => void,
    products: IProduct[], categories: IEnumerationItem[], lineOfBusinessCode: string,
    searchCode: string, searchName: string, onAdd: (p: IProduct) => void, onClose?: () => void,
    currencyCode: string, billingDetails?: IBillingDetail[]
  }) {

  const { language: lg } = useRecoilValue(currentUserSessionAtom);
  const { t, i18n } = useTranslation();

  const [codeValue, setCodeValue] = useState<string>(searchCode);
  const [nameValue, setNameValue] = useState<string>(searchName);
  const [lobValue, setLobValue] = useState<string>(lineOfBusinessCode);

  const debouncedProductFilter = useRef(
    debounce(async ({ code, name, lineOfBusinessCode }: { code: string, name: string, lineOfBusinessCode: string }) => {

      onProductFilter({ code, name, lineOfBusinessCode });

    }, 1000)
  ).current;

  useEffect(() => {
    debouncedProductFilter({ code: codeValue, name: nameValue, lineOfBusinessCode: lobValue });

  }, [codeValue, nameValue, lobValue]);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700} mb={1}>{t('Products')}</Typography>
        <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">
          {categories.map(({ code, name }: any) => (
            <MotionChip key={code} label={name} clickable
              color={lobValue === code ? 'primary' : 'default'}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.96 }}
              sx={{
                scrollSnapAlign: 'start',
                px: 1.5,
                borderRadius: '999px',
                fontWeight: 600,
                bgcolor: code === lobValue ? 'primary.main' : 'background.paper',
                color: code === lobValue ? 'primary.contrastText' : 'text.primary',
                border: '1px solid',
                borderColor: code === lobValue ? 'primary.main' : 'divider',
              }}
              onClick={() => { setLobValue(code); }} />
          ))}
        </Stack>
        <Stack spacing={1} mb={2}>
          <input
            type="text"
            placeholder={`${t('Filter by name or description')}...`}
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #ccc' }}
          />
          <input
            type="text"
            placeholder={`${t('Filter by code or barcode')}...`}
            value={codeValue}
            onChange={(e) => setCodeValue(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #ccc' }}
          />
        </Stack>
        {/* <TextField size="small" placeholder="Search product..." value={nameValue} onChange={e => setNameValue(e.target.value)} sx={{ mb: 2 }} />
        <TextField size="small" placeholder="Search product..." value={codeValue} onChange={e => setCodeValue(e.target.value)} sx={{ mb: 2 }} /> */}

        <Grid container spacing={2} key={`${lobValue} - ${searchCode} - ${searchName}`}>
          {products.map((p: any) => {
            const billingItem = (billingDetails || []).find((b) => b.productId === p.id);
            const quantity = billingItem?.quantity || 0;

            const isSelected = quantity > 0

            return (
              <Grid item xs={6} sm={4} md={3} key={p.id}>
                <motion.div whileTap={{ scale: 0.95 }}>
                  <Card
                    onClick={() => {
                      onAdd(p)
                      onClose?.()
                    }}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: '0.2s',
                      border: isSelected ? '2px solid' : '2px solid transparent',
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      '&:hover': { boxShadow: 6, },

                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      {/* BADGE */}
                      <Badge
                        badgeContent={quantity}
                        color="secondary"
                        invisible={quantity <= 0}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          '& .MuiBadge-badge': {
                            fontWeight: 700,
                            minWidth: 22,
                            height: 22,
                          },
                        }}
                      />
                      {/* IMAGE */}
                      <CardMedia
                        component="img"
                        //height="120"
                        image={`${globalConfig.get().apiUrl}/download/${p.pictureUrl}`}
                        alt={p.name}
                        sx={{
                          height: 120,
                          objectFit: 'contain',      // ✅ show full image
                          backgroundColor: '#f9f9f9',// ✅ avoid ugly empty space
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          p: 1,                      // ✅ give image breathing room
                        }}
                      />
                    </Box>
                    {/* CONTENT */}
                    <CardContent sx={{ p: 1.5, textAlign: 'center', flexGrow: 1 }}>
                      <Typography fontWeight={600} fontSize={14} noWrap>
                        {p.name}
                      </Typography>

                      <Typography color="primary" fontWeight={700}>
                        {`${new Intl.NumberFormat(lg.includes('fr') ? 'fr-FR' : 'en-US').format(p.defaultPrice)} ${currencyCode}`}
                      </Typography>

                      <Chip label={p.lineOfBusinessCode} size="small" sx={{ mt: 0.5 }} />

                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>

            )
          })}
        </Grid>
      </CardContent>
    </Card>
  )
}



/* ------------------ Command Block ------------------ */
function CommandBlock({ command, editable = false, inc, dec, sendToKitchen, toggleExpand,
  openDetail, deleteDetail, actionCommand, printCommand, isCompactLayout = false, onOpenCatalog, }:
  {
    command: IBillingCommand, editable: boolean,
    inc?: (productId: number) => void, dec?: (productId: number) => void,
    openDetail?: (detail: IBillingDetail) => void,
    deleteDetail?: (productId: number) => void,
    sendToKitchen?: (commandId: number) => void, toggleExpand?: (commandId: number) => void,

    actionCommand?: (commandId: number) => void;
    printCommand?: (commandId: number) => void;

    isCompactLayout?: boolean;
    onOpenCatalog?: () => void;
  }) {

  const { t, i18n } = useTranslation();
  const [Colors, setColors] = useRecoilState(colorsAtom);
  const { language: lg } = useRecoilValue(currentUserSessionAtom);

  const { sumBillingCommand } = useBillingService();

  // const commandTotal = command.billingDetails.reduce(
  //   (sum, d) => sum + (d.quantity ?? 0) * ((d.unityAmount ?? 0) - (d.discount ?? 0)), 0);

  const showAddButton = editable && isCompactLayout && command.status === "95"; // hide if validated

  const { id, reference } = command;
  return (
    <Card sx={{ mb: 2, borderLeft: editable ? 4 : 0, borderColor: 'primary.main' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('Command')} {id > 0 ? `# ${id} #` : ''} :: {reference}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <StatusChipIcon status={command.status} />

            {/* {command.billingDetails.length > 0 && <IconButton size="small" onClick={() => toggleExpand?.(command.id)}>{command.expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>} */}
          </Stack>
        </Stack>
        {/* <Typography variant="body2" color="text.secondary" mb={1}>
          Date: {command.date.toLocaleString()} | Ref: {command.reference} | Customer: {command.customer || '-'}
          {editable && <Button size="small" sx={{ ml: 1 }} variant="outlined">Add Customer Info</Button>}
        </Typography> */}

        <Collapse in={true} timeout="auto" unmountOnExit>
          {command.billingDetails.map((billingDetail) => {
            const lineTotal = billingDetail.quantity * billingDetail.unityAmount;

            return (
              <Box key={billingDetail.productId} display="grid"
                gridTemplateColumns="2fr 1fr 1.5fr 1fr auto"
                gap={1}
                alignItems="center"
                mb={1}>
                <Typography>{billingDetail.productName}</Typography>
                {/* Unit price */}
                <Typography variant="body2" color="text.secondary">
                  {formatAmount(billingDetail.unityAmount, lg)}
                </Typography>
                {editable ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton size="small" onClick={() => dec?.(billingDetail.productId)}>-</IconButton>
                    <Typography fontWeight={700}>{billingDetail.quantity}</Typography>
                    <IconButton size="small" onClick={() => inc?.(billingDetail.productId)}>+</IconButton>
                  </Stack>
                ) : (
                  <Typography fontWeight={700}>{billingDetail.quantity}</Typography>
                )}
                {/* Line total */}
                <Typography fontWeight={700} textAlign="right">
                  {formatAmount(lineTotal, lg)}
                </Typography>

                {/* Actions */}
                {editable && (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={t('More informations')}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openDetail?.(billingDetail)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('Remove')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteDetail?.(billingDetail.productId)}
                      >
                        ✕
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Box>
            );
          })}
          {/* ===== TOTAL ===== */}
          <Divider sx={{ my: 1 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {/* LEFT — ADD BUTTON */}
            {showAddButton ? (
              <Button
                size="small"
                variant="contained"
                startIcon={
                  <Badge
                    badgeContent={command.billingDetails.length}
                    color="secondary"
                  >
                    <RestaurantMenu />
                  </Badge>
                }
                onClick={onOpenCatalog}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {`${t('Add')} : ${t('Product')}/${t('Service')}`}
              </Button>
            ) : (
              <Box /> // keeps grid alignment
            )}

            {/* TOTAL */}
            <Typography fontWeight={800} sx={{ ml: 'auto', textAlign: 'right' }}>
              {t('Total')}:&nbsp;{formatAmount(sumBillingCommand(command, 'amount'), lg)}
            </Typography>
          </Box>

          {command.id > 0 && <Box mt={2} display="flex" justifyContent="center">
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                //color="success"
                startIcon={<MdSystemUpdateAlt color={Colors.menuButton} size={24} />}
                sx={{ minWidth: 140 }}
                onClick={() => actionCommand?.(command.id)}
              >
                {t('Action')}
              </Button>


              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                sx={{ minWidth: 140 }}
                onClick={() => printCommand?.(command.id)}
              >
                {t('Print')}
              </Button>
            </Stack>

          </Box>}

        </Collapse>



      </CardContent>
    </Card>
  )
}


interface CommandListProps {
  commands: IBillingCommand[];
  sumBillingCommand: (command: IBillingCommand, field: 'amount' | 'netAmount' | 'tax' | 'discount') => number;
  handleActionCommand: (id: number) => void;
  handlePrintCommand: (id: number) => void;
  onViewDetail: (command: IBillingCommand) => void; // new
}

export const CommandList: React.FC<CommandListProps> = ({
  commands,
  sumBillingCommand,
  handleActionCommand,
  handlePrintCommand,
  onViewDetail
}) => {
  const theme = useTheme();
  //const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { language: lg } = useRecoilValue(currentUserSessionAtom);

  const { t, i18n } = useTranslation();
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

  // Sorting state
  const [sortBy, setSortBy] = useState<keyof IBillingCommand>("id");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const sortedCommands = [...commands].sort((a, b) => {
    const aVal = a[sortBy];
    const bVal = b[sortBy];
    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    }
    return sortOrder === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const handleSort = (field: keyof IBillingCommand) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  if (isCompactLayout) {
    // MOBILE: stacked card view
    return (
      <Stack spacing={2}>
        <AnimatePresence>
          {sortedCommands.map((c) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Paper sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography>ID: {c.id}</Typography>
                  <Typography
                    sx={{ cursor: "pointer", textDecoration: "underline", color: "primary.main" }}
                    onClick={() => onViewDetail(c)}
                  >
                    {t('Reference')}: {c.reference}
                  </Typography>
                  <Typography>{t('Amount')}: {formatAmount(sumBillingCommand(c, "amount"), lg)}</Typography>
                  <StatusChipIcon status={c.status} />
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleActionCommand(c.id)}
                    >
                      <MdSystemUpdateAlt />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handlePrintCommand(c.id)}
                    >
                      <PrintIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Paper>
            </motion.div>
          ))}
        </AnimatePresence>
      </Stack>
    );
  }

  const compactCell = { py: 0.75, px: 1 };
  const MotionTableRow = motion(TableRow);

  // DESKTOP: table view
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell onClick={() => handleSort("id")} sx={{ cursor: "pointer" }}>
              ID {sortBy === "id" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
            </TableCell>
            <TableCell
              onClick={() => handleSort("reference")}
              sx={{ cursor: "pointer" }}
            >
              {t('Reference')} {sortBy === "reference" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
            </TableCell>
            <TableCell
              align="right"
              // onClick={() => handleSort("amount")}
              sx={{ cursor: "pointer" }}
            >
              {t('Amount')} {/* Amount {sortBy === "amount" ? (sortOrder === "asc" ? "▲" : "▼") : ""} */}
            </TableCell>
            <TableCell>{t('Status')}</TableCell>
            <TableCell align="center">{t('Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <AnimatePresence>
            {sortedCommands.map((c) => (
              <MotionTableRow
                key={c.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
              >
                <TableCell sx={compactCell}>{c.id}</TableCell>
                <TableCell>
                  <Typography
                    sx={{ py: 0.75, px: 1, cursor: "pointer", textDecoration: "underline", color: "primary.main" }}
                    onClick={() => onViewDetail(c)}
                  >
                    {c.reference}
                  </Typography>
                </TableCell>
                <TableCell sx={compactCell} align="right">{formatAmount(sumBillingCommand(c, "amount"), lg)}</TableCell>
                <TableCell sx={compactCell}>
                  <StatusChipIcon status={c.status} />
                </TableCell>
                <TableCell sx={compactCell} align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title="Action">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleActionCommand(c.id)}
                      >
                        <MdSystemUpdateAlt fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Print">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handlePrintCommand(c.id)}
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </MotionTableRow>
            ))}
          </AnimatePresence>
        </TableBody>
      </Table>
    </TableContainer>
  );
};


/* ------------------ Billing Summary ------------------ */
function BillingSummary({ billing, billingCommand, onViewDiscount }:
  { billing: IBilling, billingCommand: IBillingCommand, onViewDiscount: (billing: IBilling) => void }) {
  const { t, i18n } = useTranslation();

  const { language: lg, applicationSetup } = useRecoilValue(currentUserSessionAtom);

  const { openEntityActionDrawer, openEntityPrintDrawer } = entityService();

  const { sumBilling } = useBillingService();

  const {
    initialNetAmount, discount, netAmount, amount, amountPaid, unpaidAmount, status, id, } = billing;

  const calcNetAmount = sumBilling(billing, billingCommand, 'netAmount');
  const calInitialNetAmount = sumBilling(billing, billingCommand, 'initialNetAmount');
  const calcDiscount = sumBilling(billing, billingCommand, 'discount');

  const calcAmount = sumBilling(billing, billingCommand, 'amount');

  return (
    <Card sx={{
      position: "sticky",
      bottom: 0,
      zIndex: 10,
      borderTop: "1px solid",
      borderColor: "divider",
    }}>
      <CardContent>
        <Stack spacing={1} direction="column">
          <Stack direction="row">
            <Box sx={{ width: "66.66%", display: "flex", justifyContent: "space-between", px: 1 }}>
              <Typography sx={{ fontWeight: "bold", textAlign: "right" }}>{billing.reference}</Typography>
            </Box>

            <Box sx={{ width: "33.33%", display: "flex", justifyContent: "space-between", px: 1 }}>
              <StatusChipIcon status={billing.status} />
            </Box>
          </Stack>
          <Stack direction="row">
            <SummaryItem label={t("Initial")} value={formatAmount(calInitialNetAmount, lg)} />
            <SummaryItem label={t("Discount")} value={formatAmount(calcDiscount, lg)} 
                onViewItem={applicationSetup.isBillingDiscountEnabled?onViewDiscount:undefined} />
            <SummaryItem label={t("Net")} value={formatAmount(calcNetAmount, lg)} />
          </Stack>
          {/* Second row: Total | Paid | Unpaid */}
          <Stack direction="row">
            <SummaryItem label={t("Total")} value={formatAmount(calcAmount, lg)} />
            <SummaryItem label={t("Paid")} value={formatAmount(amountPaid, lg)} />
            <SummaryItem label={t("Unpaid")} value={formatAmount(unpaidAmount, lg)} />
          </Stack>
          <Divider sx={{ my: 1 }} />

          {/* Action buttons checkEntitySaveAuthorization("Sale", id)*/}
          <Stack direction="row" justifyContent="center" spacing={1}>

            {id > 0 && (
              <ActionButton
                label={t("Do an action")}
                variant={status === "10" ? "outlined" : "contained"}
                onClick={() => { openEntityActionDrawer('Billind', id); }}
              />
            )}

            {status === "10" && unpaidAmount > 0 && (
              <ActionButton label={t("Pay the billing")} variant="contained"
                onClick={() => { openEntityActionDrawer('Billind', id); }}
              />
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}

function formatAmount(value: number, lg: string) {
  return new Intl.NumberFormat(lg.includes("fr") ? "fr-FR" : "en-US").format(value || 0);
}

// === Reusable action button ===
function ActionButton({ label, variant, onClick }: { label: string; variant: "contained" | "outlined"; onClick?: () => void }) {
  return (
    <Button
      variant={variant}
      onClick={onClick}
      sx={{ width: "45%", height: 45, borderRadius: "15px", fontFamily: "Poppins !important" }}
    >
      {label}
    </Button>
  );
}

// === Reusable summary item ===
function SummaryItem({ label, value, onViewItem }:
  { label: string; value: string, onViewItem?: (data: any) => void }) {
  return (
    <Box sx={{ width: "33.33%", display: "flex", justifyContent: "space-between", px: 1 }}>
      <Typography
        onClick={onViewItem ? onViewItem : undefined}
        sx={{
          fontWeight: "bold",
          color: onViewItem ? 'primary.main' : 'text.disabled',
          cursor: onViewItem ? 'pointer' : 'default'
        }}>{label}</Typography>
      <Typography sx={{ fontWeight: "bold", textAlign: "right" }}>{value}</Typography>
    </Box>
  );
}